import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SplitType from 'split-type';
import { onCleanup } from './cleanup.js';

gsap.registerPlugin(ScrollTrigger);

/**
 * Registra el desmontaje de un lote de animaciones.
 * Sin esto, cada navegación dejaba vivos los ScrollTrigger (suscritos al
 * scroll global) y las instancias de SplitType, que retienen arrays de nodos
 * ya destruidos.
 * @param {gsap.core.Animation[]} animations Timelines o tweens a matar
 * @param {SplitType[]} splits Instancias de SplitType a revertir
 */
function registerGsapCleanup(animations, splits) {
  onCleanup(() => {
    animations.forEach((anim) => {
      anim.scrollTrigger?.kill();
      anim.kill();
    });
    splits.forEach((split) => split.revert());
  });
}

export function initTextClipAnimations() {
  const textClipElements = document.querySelectorAll('.txt-clip-anim');
  const createdAnimations = [];
  const createdSplits = [];
  // console.log('Text clip elements found:', textClipElements.length);

  textClipElements.forEach((element) => {
    // Debug del atributo data-type
    const dataTypeValue = element.getAttribute('data-type');
    // console.log('Raw data-type attribute:', dataTypeValue);

    // Obtener el tipo de split del data-attribute (default: 'chars')
    const splitType = dataTypeValue === 'words' ? 'words' : 'chars';
    // Obtener el modo de animación del data-attribute (default: 'scroll')
    const animMode = element.getAttribute('data-mode') || 'scroll';
    // Obtener el valor del scroll end del data-attribute (default: '33%')
    const scrollEnd = element.getAttribute('data-scroll-end') || '40%';
    // console.log('Split type resolved to:', splitType, 'Animation mode:', animMode, 'for element:', element);

    // Aplicar SplitType - siempre incluir words y chars para flexibilidad
    const split = new SplitType(element, {
      types: 'words, chars'
    });

    // console.log('Split result:', split);
    // console.log('Available words:', split.words);
    // console.log('Available chars:', split.chars);

    // Seleccionar elementos a animar según splitType
    const elementsToAnimate = splitType === 'words' ? split.words : split.chars;
    // console.log('Elements to animate:', elementsToAnimate);

    // Establecer estados iniciales - VERSIÓN SIMPLIFICADA SIN CLIP-PATH
    gsap.set(element, {
      overflow: 'hidden'
    });

    // Si es modo force, hacer visible el elemento antes de animar
    if (animMode === 'force') {
      gsap.set(element, { opacity: 1 });
    }

    gsap.set(elementsToAnimate, {
      y: 100, // Valor simple en pixels
      opacity: 0 // Empezar invisible para test
    });

    // Crear timeline para la animación
    let timelineConfig = {};

    // Si es modo scroll, agregar ScrollTrigger
    if (animMode === 'scroll') {
      timelineConfig.scrollTrigger = {
        trigger: element,
        start: "top 100%",
        end: `bottom ${scrollEnd}`,
        scrub: 0.5,
        // markers: true,
      };
    }

    const tl = gsap.timeline(timelineConfig);

    // VERSIÓN SIMPLE: Solo animar Y y opacity
    tl.to(elementsToAnimate, {
      y: 0,
      opacity: 1,
      duration: 0.8,
      stagger: 0.13,
      delay: animMode === 'force' ? 0.75 : 0,
      ease: "power2.out"
    });

    createdAnimations.push(tl);
    createdSplits.push(split);
  });

  registerGsapCleanup(createdAnimations, createdSplits);
}

export function initHeroSequentialText() {
  const heroContainer = document.querySelector('.hero-sequential-text');
  if (!heroContainer) return;

  const textElements = heroContainer.querySelectorAll('.txt-clip-anim');
  if (textElements.length === 0) return;

  // Preparar cada elemento con SplitType
  const splitElements = [];
  textElements.forEach((element, index) => {
    const split = new SplitType(element, {
      types: 'words, chars'
    });
    
    const elementsToAnimate = split.words;
    
    // Establecer estados iniciales
    gsap.set(element, { 
      overflow: 'hidden',
      opacity: 1
    });
    gsap.set(elementsToAnimate, {
      y: 100,
      opacity: 0
    });

    splitElements.push({
      element,
      words: elementsToAnimate,
      split,
      index
    });
  });

  const PHRASE_DURATION = 3; // Duración total de cada frase (entrada + mostrar + salida)
  const ENTER_DURATION = 0.8;
  const SHOW_DURATION = 2;
  const EXIT_DURATION = 0.7;
  const STAGGER_ENTER = 0.08;
  const STAGGER_EXIT = 0.06;

  // Calcular la duración exacta de una frase completa
  // Necesitamos sumar el tiempo de entrada + mostrar + salida + stagger adicional
  const singlePhraseTime = ENTER_DURATION + SHOW_DURATION + EXIT_DURATION;
  
  // Calcular la duración total del ciclo completo
  // La última frase necesita tiempo completo, las demás se solapan cada PHRASE_DURATION
  const totalDuration = (splitElements.length - 1 ) * PHRASE_DURATION + singlePhraseTime;

  // Crear timeline principal con loop infinito
  // Usamos duration exacta para evitar gaps en el loop
  const masterTimeline = gsap.timeline({ 
    repeat: -1,
    delay: 0,
    repeatDelay: -0.21,
    duration: totalDuration // Forzar duración exacta del ciclo
  });

  splitElements.forEach((splitEl, index) => {
    const { words } = splitEl;
    
    // Calcular el timing para cada frase
    const startTime = index * PHRASE_DURATION;
    
    // Animación de entrada
    masterTimeline.to(words, {
      y: 0,
      opacity: 1,
      duration: ENTER_DURATION,
      stagger: STAGGER_ENTER,
      ease: "power2.out"
    }, startTime)
    
    // Animación de salida
    .to(words, {
      y: -80,
      opacity: 0,
      duration: EXIT_DURATION,
      stagger: STAGGER_EXIT,
      ease: "power2.in"
    }, startTime + ENTER_DURATION + SHOW_DURATION);
  });

  // El timeline tiene repeat: -1. Si no se mata, sigue animando nodos
  // destruidos indefinidamente después de salir del home.
  registerGsapCleanup([masterTimeline], splitElements.map((s) => s.split));

  // console.log('Hero sequential animation initialized');
  // console.log('Total cycle duration:', lastPhraseEndTime, 'seconds');
}

export function clipBox() {
  const clipboxElements = document.querySelectorAll('.clipbox');
  const createdAnimations = [];

  clipboxElements.forEach((element) => {
    // Obtener el tipo de animación del data-attribute (default: 'top-bottom')
    const animType = element.getAttribute('data-type') || 'top-bottom';
    // Obtener el valor de scrub del data-attribute (default: 'true')
    const scrubMode = element.getAttribute('data-scrub') !== 'false';
    // Obtener el valor del scroll end del data-attribute (default: '40%')
    const scrollEnd = element.getAttribute('data-scroll-end') || '40%';

    // Definir los valores de clip-path según el tipo de animación
    let fromClip, toClip;

    switch (animType) {
      case 'top-bottom':
        fromClip = "polygon(100% 0, 0 0, 0 0, 100% 0)";
        toClip = "polygon(100% 0, 0 0, 0 100%, 100% 100%)";
        break;
      case 'left-right':
        fromClip = "polygon(0 0, 0 0, 0 100%, 0% 100%)";
        toClip = "polygon(0 0, 100% 0, 100% 100%, 0 100%)";
        break;
      case 'right-left':
        fromClip = "polygon(100% 0, 100% 0, 100% 100%, 100% 100%)";
        toClip = "polygon(100% 0, 0 0, 0 100%, 100% 100%)";
        break;
      case 'bottom-top':
        fromClip = "polygon(100% 100%, 0 100%, 0 100%, 100% 100%)";
        toClip = "polygon(100% 100%, 0 100%, 0 0, 100% 0)";
        break;
      default:
        fromClip = "polygon(100% 0, 0 0, 0 0, 100% 0)";
        toClip = "polygon(100% 0, 0 0, 0 100%, 100% 100%)";
    }

    // Configurar la animación según el modo scrub
    const animationConfig = {
      clipPath: toClip
    };

    // Si scrub está habilitado, usar ScrollTrigger con scrub
    if (scrubMode) {
      animationConfig.scrollTrigger = {
        trigger: element,
        start: "top 100%",
        end: `bottom ${scrollEnd}`,
        scrub: 1,
        // markers: true
      };
    } else {
      // Si scrub está deshabilitado, usar ScrollTrigger sin scrub (one-time trigger)
      animationConfig.duration = 0.8;
      animationConfig.ease = "power2.out";
      animationConfig.scrollTrigger = {
        trigger: element,
        start: "top 80%",
        toggleActions: "play none none none"
      };
    }

    createdAnimations.push(
      gsap.fromTo(element,
        {
          clipPath: fromClip
        },
        animationConfig
      )
    );
  });

  registerGsapCleanup(createdAnimations, []);
}


